/**
 * «Работает ли» раздела EO «Виджет» (01.11, ТЗ 1.17 EMB-019 ч. 3, §0.6 п. 68(6), п. 70; INTERNAL 0.5 §6.3, §6.7, §7): сводная панель
 * без ПДн. По экземпляру прокладки — последняя сводка и «молчит» (правило тревоги: дольше 15 мин), перезапуски, самопроверка,
 * скрытность, внешняя проверка OPS-012 («нет данных» — канала нет), список доменов. По адресу кода установки — экземпляр. По
 * сайту — запросы пар «домен × адрес», последняя заявка, согласие, ключ. Тревоги — открытые (то же правило, что шлёт крон) и за
 * 7 дней со временем доставки; время снятия и повторы V4 не хранит (по коду — последняя отправка). У открытой — отправка этого
 * случая; прошлая отправка того же кода «доставкой» не называется (ревью b235d4e4). Статусы — словами и цветом (widgetStatus.ts).
 */
import { BASE, useLoad } from "./widgetApi";
import { ago, NO_DATA, numOr, orenTime, secs } from "./widgetFmt";
import { alertTone, hostVerdict, instanceVerdict, siteVerdict, TONE_LABEL, type Verdict } from "./widgetStatus";
import type { Health, InstanceHealth } from "./widgetTypes";
import css from "./widget.module.css";

const Badge = ({ v, text }: { v: Verdict; text?: string }) => <span className={`${css.badge} ${css[v.tone]}`}>{text ?? v.label}</span>;
const Reasons = ({ v }: { v: Verdict }) => (v.reasons.length ? <ul className={css.reasons}>{v.reasons.map((r) => <li key={r}>{r}</li>)}</ul> : null);
const yes = (b: boolean | null | undefined, ok: string, bad: string) => (b == null ? NO_DATA : b ? ok : bad);
const Cell = ({ k, v }: { k: string; v: string }) => <div className={css.cell}><span>{k}</span><b>{v}</b></div>;

function Instance({ i, active, now }: { i: InstanceHealth; active: boolean; now: number }) {
  const v = instanceVerdict(i, active, now), s = i.synth, o = i.origins;
  return (
    <div className={css.item}>
      <div className={css.line}><Badge v={v} /><b>экземпляр {i.id}</b><span className={css.mono}>{i.host ?? NO_DATA}</span></div>
      <Reasons v={v} />
      <div className={css.grid}>
        <Cell k="последняя сводка" v={i.last_at ? `${orenTime(i.last_at)} (${ago(i.last_at, now)})` : NO_DATA} />
        <Cell k="сводок за сутки (норма 288)" v={String(i.windows_24h)} />
        <Cell k="перезапусков за час" v={numOr(i.restarts_1h)} />
        <Cell k="версия · контракт" v={`${i.ver ?? NO_DATA} · ${yes(i.contract_ok, "как у V4", "другой")}`} />
        <Cell k="V4 пускает" v={i.v4_auth === "ok" ? "да" : i.v4_auth === "off" ? "нет" : NO_DATA} />
        <Cell k="самопроверка" v={s ? `${yes(s.ok, "прошла", "провал")}${s.fails_in_row ? `, подряд ${s.fails_in_row}` : ""} · ${orenTime(s.at)}` : NO_DATA} />
        <Cell k="загрузчик · сертификат" v={s ? `${yes(s.loader_ok, "ок", "сбой")} · ${numOr(s.tls_days, " дн.")}` : NO_DATA} />
        <Cell k="возраст расписания" v={s ? secs(s.gen_age_s) : NO_DATA} />
        <Cell k="проверка скрытности" v={i.stealth ? `${yes(i.stealth.ok, "чисто", "видно Cloudflare")} · ${orenTime(i.stealth.at)}` : NO_DATA} />
        <Cell k="внешняя проверка (OPS-012)" v={NO_DATA} />
        <Cell k="список доменов" v={o ? `${numOr(o.count)} · ${o.source === "v4" ? "от V4" : o.source === "file" ? "копия с диска" : o.source === "none" ? "нет" : NO_DATA} · ${secs(o.age_s)}` : NO_DATA} />
      </div>
    </div>
  );
}

export function WidgetHealth() {
  const h = useLoad<Health>(`${BASE}/v2/health`);
  const d = h.data, now = d ? Date.parse(d.now) : Date.now();
  return (
    <div className={css.card}>
      <div className={css.head}>
        <span className={css.title}>Работает ли</span>
        <div className={css.line}>
          {d ? <span className={css.muted}>на {orenTime(d.now)}</span> : null}
          <button type="button" className={css.ghost} disabled={h.busy} onClick={h.reload}>Обновить</button>
        </div>
      </div>
      {h.err ? <div className={css.err}>{h.err}</div> : null}
      {!d && !h.err ? <div className={css.muted}>Загружаю…</div> : null}
      {d ? (
        <>
          {!d.proxies_ok ? <p className={css.warnLine}>Экземпляров прокладки нет: V2_PROXIES пуст или с ошибкой — шлюз закрыт{d.active_key ? ", а сайт v2 включён: заявки не доходят" : ""}. Меняется только выкладкой V4.</p> : null}
          <div className={css.sub}>Экземпляры прокладки (из V2_PROXIES, только чтение)</div>
          {d.instances.map((i) => <Instance key={i.id} i={i} active={d.active_key} now={now} />)}
          <div className={css.sub}>Адреса кода установки</div>
          {!d.hosts.length ? <div className={css.muted}>{NO_DATA}: V2_API_HOSTS пуст</div> : null}
          {d.hosts.map((x) => { const v = hostVerdict(x); return (
            <div key={x.host} className={css.line}><Badge v={v} /><b className={css.mono}>{x.host}</b>
              <span className={css.muted}>{x.role === "primary" ? "основной" : "запасной"} · {x.instances.length ? `экземпляр ${x.instances.join(", ")}` : v.reasons.join("; ")}</span></div>); })}
          {d.hosts.length === 1 ? <p className={css.warnLine}>Нет запасного адреса</p> : null}
          <div className={css.sub}>Сайты</div>
          {!d.sites.length ? <div className={css.muted}>Сайтов v2 нет</div> : null}
          {d.sites.map((s) => { const v = siteVerdict(s); return (
            <div key={s.id} className={css.item}>
              <div className={css.line}><Badge v={v} /><b>{s.domain}</b>
                <span className={css.muted}>ключ {s.active ? "действует" : "отозван"} · v2 {s.enabled ? "включён" : "выключен"} · согласие {s.pd ? `${s.pd.version}` : "нет"} · последняя заявка {s.last_request_at ? `${orenTime(s.last_request_at)} (${ago(s.last_request_at, now)})` : NO_DATA}</span></div>
              <Reasons v={v} />
              {s.seen.map((p) => <div key={`${p.domain}-${p.host}-${p.instance}`} className={css.row}>{p.domain} × {p.host} ({p.instance}): последний запрос {orenTime(p.last_at)} ({ago(p.last_at, now)})</div>)}
            </div>); })}
          <div className={css.sub}>Тревоги — открытые сейчас</div>
          {!d.alerts.open.length ? <div className={css.muted}>открытых тревог нет</div> : null}
          {d.alerts.open.map((a) => { const tone = alertTone(a.code); return (
            <div key={a.code} className={css.line}><span className={`${css.badge} ${css[tone]}`}>{TONE_LABEL[tone]}</span><span className={css.row}>{a.text}</span>
              <span className={css.muted}>{a.sent_at ? `доставлена в TG ${orenTime(a.sent_at)}` : a.next_at
                ? `в TG ещё не ушла: этот код уходил ${orenTime(a.last_sent_at)} (прошлый случай), повтор — не раньше ${orenTime(a.next_at)}`
                : "ещё не доставлена (ближайший тик — за 5 мин)"}</span></div>); })}
          <div className={css.sub}>Тревоги за 7 дней — по коду последняя отправка (повторы V4 не хранит)</div>
          {!d.alerts.history.length ? <div className={css.muted}>не было</div> : null}
          {d.alerts.history.map((a) => (
            <div key={`${a.code}-${a.sent_at}`} className={css.row}>{orenTime(a.sent_at)} · {a.code} · {a.open ? "держится" : `снята (время снятия: ${NO_DATA})`}</div>
          ))}
          <span className={css.muted}>Сводка 23:00 (OPS-006): {d.alerts.daily_sent_at ? `ушла ${orenTime(d.alerts.daily_sent_at)}` : NO_DATA}</span>
        </>
      ) : null}
    </div>
  );
}
